import React from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import Checkbox from '@grnhse/seedling/lib/birch/components/checkbox';
import { useController } from '@grnhse/seedling/lib/birch/components/forms/form';
import MultiSelectField from '@grnhse/seedling/lib/birch/components/forms/fields/multi_select_field';
import TextInput from '@grnhse/seedling/lib/birch/components/forms/text_input';
import { HelpTooltip } from '@grnhse/seedling/lib/birch/components/tooltips';
import Container from '@grnhse/seedling/lib/birch/components/container';
import styled from 'styled-components';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import { withLocalePrefix } from 'shared/utils/translation';
import { CalibrationSettings } from 'talent_matching/types';
import WorkTitleField from '../work_title_field';
import AiMagicIcon from '@grnhse/seedling/lib/birch/icons/ai_magic';
import { useCalibrationModal } from 'talent_matching/contexts/calibration_modal_context';
import { useIndustries } from 'talent_matching/api';

const t = withLocalePrefix('talent_matching.modals.calibration');

const DetailsContainer = styled(Container)`
  background-color: ${FullPalette.WHITE};
  border-color: ${FullPalette.GREY_600};
  box-shadow: none;
`;

type Props = {
  disabled: boolean;
  calibration?: CalibrationSettings;
  jobTitles: string[];
  onAddWorkTitle: (titles: string[]) => void;
  onRemoveWorkTitle: (keyword: string) => void;
  popperRef: React.RefObject<HTMLElement>;
};

const CalibrationDetailsPanel = ({
  disabled: isDisabled,
  calibration,
  jobTitles,
  onAddWorkTitle,
  onRemoveWorkTitle,
  popperRef,
}: Props) => {
  const { paths } = useCalibrationModal();
  const { data: industryOptions } = useIndustries(paths, !isDisabled);
  const jobTitlesForField = calibration?.job_titles ?? jobTitles;
  const minYearsFromCalibration = calibration?.min_years_experience ?? '';

  const {
    field: prioritizeCurrentExperienceField,
    fieldState: prioritizeCurrentExperienceFieldState,
  } = useController({
    name: 'prioritize_current_experience',
  });

  const {
    field: minYearsExperienceField,
    fieldState: minYearsExperienceFieldState,
  } = useController({
    name: 'min_years_experience',
  });

  const {
    field: includeSimilarRolesField,
    fieldState: includeSimilarRolesFieldState,
  } = useController({
    name: 'use_boolean_search',
  });

  const prioritizeCurrentExperience =
    isDisabled && calibration
      ? !!calibration.prioritize_current_experience
      : !!prioritizeCurrentExperienceField.value;

  const includeSimilarRoles =
    isDisabled && calibration
      ? !!calibration.use_boolean_search
      : !!includeSimilarRolesField.value;

  const minYearsExperience =
    isDisabled && calibration
      ? minYearsFromCalibration
      : minYearsExperienceField.value ?? '';

  const minYearsExperienceValue =
    minYearsExperience === 0
      ? '0'
      : typeof minYearsExperience === 'number'
      ? minYearsExperience.toString()
      : minYearsExperience || '';

  return (
    <DetailsContainer>
      <Stack spacing={2}>
        <Body medium>{t('phase2.additional_details.title')}</Body>
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Checkbox
              label={t('phase2.additional_details.prioritize_experience')}
              value={'prioritize_current_experience'}
              checked={prioritizeCurrentExperience}
              error={!!prioritizeCurrentExperienceFieldState.error}
              helperText={prioritizeCurrentExperienceFieldState.error?.message}
              disabled={isDisabled}
              onChange={(checked) => {
                if (isDisabled) {
                  return;
                }

                prioritizeCurrentExperienceField.onChange(checked);
              }}
            />
            {!isDisabled && (
              <HelpTooltip
                ariaLabel={t(
                  'phase2.additional_details.prioritize_experience_tooltip'
                )}
                label={t(
                  'phase2.additional_details.prioritize_experience_tooltip'
                )}
              />
            )}
          </Stack>
          <TextInput
            label={t('phase2.additional_details.years_of_experience')}
            type="number"
            min={0}
            max={50}
            disabled={isDisabled}
            error={!!minYearsExperienceFieldState.error}
            helperText={minYearsExperienceFieldState.error?.message}
            onChange={minYearsExperienceField.onChange}
            onBlur={minYearsExperienceField.onBlur}
            value={minYearsExperienceValue}
            inputRef={minYearsExperienceField.ref}
          />
          <MultiSelectField
            name="industries"
            label={t('additional_details.industry')}
            items={industryOptions}
            disabled={isDisabled}
          />
          <Stack spacing={1}>
            <WorkTitleField
              onAdd={isDisabled ? () => undefined : onAddWorkTitle}
              onRemove={isDisabled ? () => undefined : onRemoveWorkTitle}
              values={isDisabled ? jobTitlesForField : jobTitles}
              inputRef={popperRef}
              booleanIndicator={'or'}
              disabled={isDisabled}
            />
            <Checkbox
              data-provides={'talent-matching-include-similar-roles'}
              label={t('additional_details.include_similar_roles')}
              CustomLabel={({ label }) => (
                <Body>
                  {label} <AiMagicIcon aria-hidden />
                </Body>
              )}
              value={'use_boolean_search'}
              checked={includeSimilarRoles}
              error={!!includeSimilarRolesFieldState.error}
              helperText={includeSimilarRolesFieldState.error?.message}
              disabled={isDisabled}
              onChange={(checked) => {
                if (isDisabled) {
                  return;
                }

                includeSimilarRolesField.onChange(checked);
              }}
            />
          </Stack>
        </Stack>
      </Stack>
    </DetailsContainer>
  );
};

export default CalibrationDetailsPanel;
